import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Gamepad2, Globe } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';

interface HomePageProps {
  onCreateRoom: () => void;
  onJoinRoom: () => void;
  onLocalMode: () => void;
}

export const HomePage: React.FC<HomePageProps> = ({
  onCreateRoom,
  onJoinRoom,
  onLocalMode,
}) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Main Content */}
      <div className="text-center mb-12 animate-fade-in">
        <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 font-display">
          {t('home.title')}
        </h1>
        <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-2xl mx-auto">
          {t('home.subtitle')}
        </p>
        <p className="text-lg text-white/60 max-w-3xl mx-auto">
          {t('home.description')}
        </p>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl animate-slide-up">
        {/* Create Room */}
        <Card variant="gradient" hover className="text-center group">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
              <Plus className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">
              {t('home.createRoom')}
            </h3>
            <p className="text-white/70">
              {t('home.createRoomDescription')}
            </p>
          </div>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={onCreateRoom}
            icon={<Plus className="w-5 h-5" />}
          >
            {t('home.createRoom')}
          </Button>
        </Card>

        {/* Join Room */}
        <Card variant="gradient" hover className="text-center group">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">
              {t('home.joinRoom')}
            </h3>
            <p className="text-white/70">
              {t('home.joinRoomDescription')}
            </p>
          </div>
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onJoinRoom}
            icon={<Users className="w-5 h-5" />}
          >
            {t('home.joinRoom')}
          </Button>
        </Card>

        {/* Local Mode */}
        <Card variant="gradient" hover className="text-center group">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-accent-400 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
              <Gamepad2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">
              {t('home.localMode')}
            </h3>
            <p className="text-white/70">
              {t('home.localModeDescription')}
            </p>
          </div>
          <Button
            variant="accent"
            size="lg"
            fullWidth
            onClick={onLocalMode}
            icon={<Gamepad2 className="w-5 h-5" />}
          >
            {t('home.localMode')}
          </Button>
        </Card>
      </div>

      {/* Features */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-4xl">
        <div className="text-center">
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Globe className="w-6 h-6 text-white" />
          </div>
          <h4 className="text-white font-semibold mb-2">Multi-idioma</h4>
          <p className="text-white/60 text-sm">Español e Inglés</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Users className="w-6 h-6 text-white" />
          </div>
          <h4 className="text-white font-semibold mb-2">3-10 Jugadores</h4>
          <p className="text-white/60 text-sm">Perfecto para grupos</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Gamepad2 className="w-6 h-6 text-white" />
          </div>
          <h4 className="text-white font-semibold mb-2">Modo Local</h4>
          <p className="text-white/60 text-sm">Sin conexión a internet</p>
        </div>
      </div>
    </div>
  );
};
